import ControllerBase from '../../../../global/js/ControllerBase';
import {fromEvent, Observable, of, Subject} from 'rxjs';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {fromFetch} from 'rxjs/fetch';
import {ModalFinderPopupFormResponseModel} from './m-finder-popup-form.model';
import {HttpActionResponse} from 'global/interfaces/http.interface';
import {ComponentEvents} from 'global/interfaces/component.interface';

import {MoleculeAlertProps} from '../m-alert/m-alert.data';

const alertTemplate = require('components/molecules/m-alert/m-alert.template.twig') as any;

enum SubmitButtonUseCases {
    Disable,
    Enable,
    Done
}

export default class ControllerFinderPopupForm extends ControllerBase {
    private readonly componentRefs = {
        alertWrapper: 'alertWrapper',
        formWrapper: 'formFinderWrapper',
        submitBtn: 'submitBtn',
        spinnerWrapper: 'spinnerWrapper',
        submitText: 'submitText',
        doneWrapper: 'doneWrapper'
    };

    private componentEvents: ComponentEvents = {
        success: '',
        failure: '',
    };

    /** Subscription management */
    private _unsubscribeAll: Subject<any>;

    private formUrl: string;

    constructor(element: HTMLElement) {
        super(element);

        this.init();
        this.observeFormSubmissions();
    }

    private init() {
        this._unsubscribeAll = new Subject();
        window.addEventListener('unload', (e) => {
            this._unsubscribeAll.next();
            this._unsubscribeAll.complete();
        });

        this.formUrl = <string>this.$element.dataset.formurl;

        this.componentEvents.success = this.$element.dataset.eventNameSuccess || '';
        this.componentEvents.failure = this.$element.dataset.eventNameFailure || '';
    }

    /**
     * Returns the form html element
     * @returns HTMLFormElement
     */
    private getForm(): HTMLFormElement | undefined {
        const form = <HTMLFormElement>(this.$references[this.componentRefs.formWrapper]);
        if (form) {
            return form;
        }
    }

    private getSubmitButton(): HTMLElement | undefined {
        const submitBtn = <HTMLElement>(this.$references[this.componentRefs.submitBtn]);
        if (submitBtn) {
            return submitBtn;
        }
    }

    private getSpinnerWrapper(): HTMLElement | undefined {
        const spinnerWrapper = <HTMLElement>(this.$references[this.componentRefs.spinnerWrapper]);
        if (spinnerWrapper) {
            return spinnerWrapper;
        }
    }

    private getSubmitText(): HTMLElement | undefined {
        const submitText = <HTMLElement>(this.$references[this.componentRefs.submitText]);
        if (submitText) {
            return submitText;
        }
    }

    private getDoneWrapper(): HTMLElement | undefined {
        const doneWrapper = <HTMLElement>(this.$references[this.componentRefs.doneWrapper]);
        if (doneWrapper) {
            return doneWrapper;
        }
    }

    /**
     * Watch for form submitted events
     * @returns void
     */
    private observeFormSubmissions(): void {
        const form = this.getForm();
        if (form) {
            fromEvent(form, 'submit')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((e) => {
                    this.handleDisableSubmitButton(SubmitButtonUseCases.Disable)
                    e && e.preventDefault();
                    // send a form submission to the API
                    this.submitForm()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(
                            (response) => {
                                //after 30 secs reanable it
                                setTimeout(() => this.handleDisableSubmitButton(SubmitButtonUseCases.Enable), 30000);

                                if (response) {
                                    if (response.message) {
                                        this.handleDisableSubmitButton(SubmitButtonUseCases.Done)
                                        this.showSubmissionAlert(response.message);
                                    }
                                }

                                if (response?.data?.success) {
                                    this.handleDisableSubmitButton(SubmitButtonUseCases.Done)
                                    // emit eventNameSuccess
                                    this.componentEvents.success
                                        ? this.dispatchCustomEvent<ModalFinderPopupFormResponseModel>(this.componentEvents.success, response.data, true)
                                        : undefined;
                                } else {
                                    this.handleDisableSubmitButton(SubmitButtonUseCases.Enable)
                                    // emit eventNameFailure
                                    this.componentEvents.failure
                                        ? this.dispatchCustomEvent<false>(this.componentEvents.success, false, true)
                                        : undefined;
                                }
                            },
                            (response) => {
                                this.handleDisableSubmitButton(SubmitButtonUseCases.Enable)
                                /** show generic error message */
                                return {
                                    alert: {
                                        items: [
                                            {
                                                message: response.text(),
                                                type: 'danger',
                                            },
                                        ],
                                    },
                                };
                            },
                        );
                });
        }
    }


    private handleDisableSubmitButton(disable: SubmitButtonUseCases) {
        const submitBtn = this.getSubmitButton();
        const spinnerWrapper = this.getSpinnerWrapper();
        const submitText = this.getSubmitText();
        const doneWrapper = this.getDoneWrapper();
        switch (disable) {
            case SubmitButtonUseCases.Disable:
                submitBtn?.setAttribute('disabled', 'disabled');
                spinnerWrapper?.setAttribute("style", "display: unset;");
                submitText?.setAttribute("style", "display: none;");
                break;
            case SubmitButtonUseCases.Enable:
                submitBtn?.removeAttribute('disabled');
                spinnerWrapper?.setAttribute("style", "display: none;");
                submitText?.setAttribute("style", "display: block;");
                break
            case SubmitButtonUseCases.Done:
                submitBtn?.setAttribute('disabled', 'disabled');
                spinnerWrapper?.setAttribute("style", "display: none;");
                submitText?.setAttribute("style", "display: none;");
                doneWrapper?.setAttribute("style", "display: unset;");
                break;

        }

    }

    /**
     * Returns an observable of the API response
     * @returns Observable
     */
    private submitForm(): Observable<HttpActionResponse<ModalFinderPopupFormResponseModel, MoleculeAlertProps> | undefined> {
        const form = this.getForm();
        if (form) {
            // get the form's submit URL
            const submitUrl = this.getFormSubmitUrl();
            return fromFetch<HttpActionResponse<ModalFinderPopupFormResponseModel, MoleculeAlertProps>>(submitUrl.toString(), {
                selector: (response) => response.json(),
                method: 'POST',
                body: JSON.stringify(this.getFormData()),
            }).pipe(
                map((p) => {
                    if (p && p) {
                        return p;
                    }
                    // @todo - might be a message from the server here...
                    return of(undefined);
                }),
                catchError((e) => {
                    // handle error case or non-20x response
                    return of(e);
                }),
            );
        }
        return of(undefined);
    }

    /**
     * Determines the form's submit URL for XHR submissions
     * @returns URL
     */
    private getFormSubmitUrl(): URL {
        const form = this.getForm();

        // set the url, based on the data attrib, or the form action
        let url = this.formUrl;

        if (this.formUrl.length === 0) {
            url = form ? form.getAttribute('action') || '' : '';
        }

        return new URL(url);
    }

    /**
     * Returns the form data
     * @returns FormData
     */
    private getFormData() {
        const form = this.getForm();
        const data = {};

        if (form) {
            const inputs = form.querySelectorAll('input.js-input');
            const message: HTMLTextAreaElement | null = form.querySelector('textarea.js-input');


            inputs.forEach((input: HTMLInputElement) => {
                data[input.name] = input.value
            })

            if (message) {
                data['message'] = message.value
            }
        }

        return data
    }

    /**
     * Show a success or error message on the page after form submission
     * @param  {MoleculeAlertProps} alert
     * @returns void
     */
    private showSubmissionAlert(alert: MoleculeAlertProps): void {
        const alertDiv = this.$references[this.componentRefs.alertWrapper];
        if (alert && alertDiv) {
            const alertHtml = alertTemplate({
                data: alert,
            });
            alertDiv.innerHTML = alertHtml;
        }
    }
}
